import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useApi } from "../utils/Api";
import foodImage from "../assets/gallery3.avif";

const WHATSAPP_NUMBER = "447368325136";

// Shared Tailwind classes
const infoCard = "bg-gradient-to-b from-[#161616] to-[#0d0d0d] border border-white/10 rounded-2xl p-5 shadow-[0_10px_30px_rgba(0,0,0,0.35)]";
const whatsappLink = "flex w-full items-center justify-center gap-2 mt-5 mb-3 px-4 py-2.5 rounded-full border border-[#248648]/40 bg-[#248648]/10 font-semibold text-[14px] no-underline text-[#3ddc84] transition-all hover:bg-[#248648]/20";

export default function OrderTracking() {
  const { apiFetch } = useApi();
  const [status, setStatus] = useState("to do");
  const [orderId, setOrderId] = useState("");
  const [orderType, setOrderType] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const encryptedId = searchParams.get("id");

  const formatApiStatus = (value) => String(value || "").replace(/[_-]+/g, " ").trim().replace(/\b\w/g, (c) => c.toUpperCase());

  useEffect(() => {
    if (!encryptedId) { setError("Missing order ID in URL"); setLoading(false); return; }
    let interval = null; let isMounted = true; let inFlight = false; let firstLoad = true;
    async function fetchStatus() {
      if (inFlight) return; inFlight = true;
      try {
        const data = await apiFetch("order-status", { encrypted_order_id: encryptedId });
        if (!isMounted) return;
        if (!data.success || !data.order) { if (firstLoad) { setError(data.message || "Invalid order"); setLoading(false); } return; }
        const order = data.order;
        setStatus(String(order.status || "").trim());
        setOrderId(order.id || ""); setOrderType(String(order.order_type || "").toLowerCase().trim());
        setLoading(false); setError(null); firstLoad = false;
      } catch (err) {
        if (!isMounted) return; console.error("Failed to fetch order:", err);
        if (firstLoad) { setError("Network or server error"); setLoading(false); }
      } finally { inFlight = false; firstLoad = false; }
    }
    fetchStatus(); interval = setInterval(() => fetchStatus(), 10000);
    return () => { isMounted = false; if (interval) clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encryptedId]);

  const lowerStatus = String(status || "").toLowerCase().trim();
  const steps = [
    { key: "to do", label: "Order Received", subtext: "Your order is in queue", icon: "fa-receipt" },
    { key: "in progress", label: "Being Prepared", subtext: "Your order is being prepared.", icon: "fa-kitchen-set" },
    { key: "completed", label: "Order Ready", subtext: "Your order is ready for collection.", icon: "fa-bag-shopping" },
  ];
  const originalTrackingConfig = useMemo(() => {
    const stepOrder = { "to do": 0, "in progress": 1, completed: 2 };
    const currentStep = stepOrder[lowerStatus] ?? 0;
    return { currentStep };
  }, [lowerStatus]);

  const pageBase = "min-h-screen bg-black text-white px-[5%] pb-16 max-w-[460px] mt-[110px] mx-auto font-poppins";
  const orderTypeLabel = orderType === "pick up" ? "Pick Up" : orderType ? formatApiStatus(orderType) : "Order";

  if (loading) {
    return (
      <div className={pageBase}>
        <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-white/15 border-t-brand-orange" />
          <p className="text-[15px] text-white/60">Loading order status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={pageBase}>
        <div className="flex flex-col items-center gap-3 py-20 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#ff5252]/10 text-[#ff5252] text-2xl">
            <i className="fas fa-triangle-exclamation" />
          </div>
          <p className="font-prata text-lg font-bold">Something went wrong</p>
          <p className="text-sm text-white/50 max-w-[280px]">{error}</p>
        </div>
      </div>
    );
  }

  if (lowerStatus === "cancelled") {
    return (
      <div className={pageBase}>
        <header className="flex items-center gap-3 flex-wrap mb-5 pb-4 border-b border-[rgba(255,82,82,0.3)]">
          <h1 className="font-prata font-bold text-xl text-[#ff5252] flex items-center gap-2.5"><i className="fas fa-circle-xmark" /> Order Cancelled</h1>
        </header>
        <p className="text-sm text-white/50 mb-4">Order ID: <span className="text-white font-semibold">L-{orderId}</span></p>
        <div className="px-5 py-4 bg-[rgba(255,82,82,0.08)] border border-[rgba(255,82,82,0.4)] rounded-2xl text-[#ff5252] font-medium text-center">
          This order will not be processed.
        </div>
        <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className={whatsappLink}>
          <i className="fab fa-whatsapp text-lg" /> Chat with us on WhatsApp
        </a>
        <div className="mt-5 rounded-2xl overflow-hidden"><img src={foodImage} alt="Order" className="w-full block" /></div>
      </div>
    );
  }

  return (
    <div className={pageBase}>
      <header className="flex items-center justify-between gap-3 flex-wrap mb-6 pt-2">
        <h1 className="font-prata font-bold text-xl">Order L-{orderId}</h1>
        <span className="px-3 py-1 rounded-full bg-brand-orange/10 border border-brand-orange/30 text-brand-orange text-xs font-bold uppercase tracking-wide">
          {orderTypeLabel}
        </span>
      </header>

      <div className={`${infoCard} mb-4`}>
        {steps.map((step, index) => {
          const isLast = index === steps.length - 1;
          // Reaching the final step means the order is fully done, not
          // still "in progress" on it — so treat it as done, not current.
          const isDone = index < originalTrackingConfig.currentStep || (isLast && index === originalTrackingConfig.currentStep);
          const isCurrent = index === originalTrackingConfig.currentStep && !isDone;
          const isActive = isDone || isCurrent;
          return (
            <div key={step.key} className="flex gap-3.5">
              <div className="flex flex-col items-center">
                <span className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[13px] transition-colors duration-500 ${
                  isDone ? "bg-brand-orange text-black" : isCurrent ? "bg-brand-orange/15 text-brand-orange ring-2 ring-brand-orange" : "bg-white/[0.06] text-white/30"
                }`}>
                  {isCurrent && (
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-orange/40" />
                  )}
                  <i className={`fas ${isDone ? "fa-check" : step.icon} relative`} />
                </span>
                {!isLast && (
                  <span className={`w-[2px] flex-1 min-h-[28px] my-1 rounded-full transition-colors duration-500 ${isDone ? "bg-brand-orange" : "bg-white/10"}`} />
                )}
              </div>
              <div className={`pb-5 transition-opacity duration-500 ${isActive ? "opacity-100" : "opacity-45"}`}>
                <p className={`text-[15px] m-0 ${isCurrent ? "font-bold text-white" : "font-medium text-white/90"}`}>{step.label}</p>
                <small className="text-xs text-white/40 mt-0.5 block">{step.subtext}</small>
              </div>
            </div>
          );
        })}
      </div>

      <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className={whatsappLink}>
        <i className="fab fa-whatsapp text-lg" /> Chat with us on WhatsApp
      </a>

      <div className="mt-5 rounded-2xl overflow-hidden">
        <img src={foodImage} alt="Order" className="w-full block" />
      </div>
    </div>
  );
}
